import React, { useState } from "react";
import {
  CheckCircleIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);

    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: "📧",
      title: "Email",
      info: "rashida@englishjanala.com",
      color: "from-blue-100 to-indigo-100",
    },
    {
      icon: "📱",
      title: "Phone",
      info: "+880 1234-567890",
      color: "from-green-100 to-emerald-100",
    },
    {
      icon: "📍",
      title: "Location",
      info: "Dhaka, Bangladesh",
      color: "from-purple-100 to-violet-100",
    },
    {
      icon: "🕒",
      title: "Availability",
      info: "Mon-Sat: 9AM-8PM",
      color: "from-orange-100 to-red-100",
    },
  ];

  const socialLinks = [
    {
      name: "Facebook",
      icon: "/images/fb-thumb.png",
      color: "bg-blue-600 hover:bg-blue-700",
    },
    {
      name: "GitHub",
      icon: "/images/github-thumb.png",
      color: "bg-gray-800 hover:bg-gray-900",
    },
    {
      name: "YouTube",
      icon: "/images/youtube-thumb.png",
      color: "bg-red-600 hover:bg-red-700",
    },
    {
      name: "Instagram",
      icon: "/images/instagram-thumb.png",
      color: "bg-pink-600 hover:bg-pink-700",
    },
  ];

  const stats = [
    { number: "5+", label: "Years Experience" },
    { number: "1200+", label: "Happy Students" },
    { number: "4.9/5", label: "Average Rating" },
    { number: "24/7", label: "Support Available" },
  ];

  return (
    <section
      id="contact"
      className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 animate-gradient"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl lg:text-6xl font-bold font-display mb-6">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to start your English learning journey? Let's discuss your
            goals and create a personalized plan
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8" data-aos="fade-right">
            <div
              className="bg-white rounded-3xl p-8 shadow-xl"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <h3 className="text-2xl font-bold font-display text-gray-800 mb-8">
                Contact Information
              </h3>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div
                    key={index}
                    className={`bg-gradient-to-r ${item.color} p-6 rounded-2xl flex items-center space-x-4`}
                    data-aos="fade-right"
                    data-aos-delay={400 + index * 100}
                  >
                    <div className="text-3xl">{item.icon}</div>
                    <div>
                      <p className="font-semibold text-gray-800">
                        {item.title}
                      </p>
                      <p className="text-gray-600">{item.info}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="bg-white rounded-3xl shadow-2xl p-8"
            data-aos="fade-left"
          >
            {isSubmitted ? (
              <div className="text-center py-16" data-aos="zoom-in">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-3xl font-bold font-display text-gray-800 mb-4">
                  Message Sent Successfully!
                </h3>
                <p className="text-gray-600 text-lg">
                  Thank you for reaching out. I'll get back to you within 24
                  hours.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="text-3xl font-bold font-display text-gray-800 mb-8"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  Send Me a Message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div data-aos="fade-up" data-aos-delay="400">
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        placeholder="Your full name"
                      />
                    </div>
                    <div data-aos="fade-up" data-aos-delay="500">
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div data-aos="fade-up" data-aos-delay="600">
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        placeholder="+880 1234-567890"
                      />
                    </div>
                    <div data-aos="fade-up" data-aos-delay="700">
                      <label
                        htmlFor="service"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Service Interested In *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      >
                        <option value="">Select a service</option>
                        <option value="one-on-one">One-on-One Tutoring</option>
                        <option value="group">Group Classes</option>
                        <option value="ielts">IELTS Preparation</option>
                        <option value="business">Business English</option>
                      </select>
                    </div>
                  </div>

                  <div data-aos="fade-up" data-aos-delay="800">
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all resize-none"
                      placeholder="Tell me about your English learning goals and how I can help you..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
                    data-aos="fade-up"
                    data-aos-delay="900"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        <span>Sending Message...</span>
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-6 h-6" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
